// DB-first-with-static-fallback content overrides.
//
// Fetches the full set of admin-set page overrides ONCE per SPA session
// (module-level cache, deduped in-flight), and exposes lookups. Every override
// is optional — callers merge with their static default: `override?.hero_image
// || data.heroImage`. Fails open (empty map) so the site always renders the
// static defaults even if the backend is unreachable.

import { useEffect, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

let _cache = null; // { [pageKey]: overrideDoc } — survives route navigations
let _cachePromise = null; // in-flight de-dupe

async function fetchOverrides() {
    if (_cache) return _cache;
    if (_cachePromise) return _cachePromise;
    _cachePromise = axios
        .get(`${BACKEND_URL}/api/page-content`)
        .then((res) => {
            const map = {};
            (Array.isArray(res.data) ? res.data : []).forEach((o) => {
                if (o && o.pageKey) map[o.pageKey] = o;
            });
            _cache = map;
            return map;
        })
        .catch(() => {
            // Fail open (static defaults render) but do NOT cache the empty
            // result — leave the cache unset so the next lookup retries once
            // the backend recovers (a transient first-fetch failure otherwise
            // disables all overrides for the whole session).
            _cachePromise = null;
            return {};
        });
    return _cachePromise;
}

/** Look up a single override by pageKey. Returns undefined until loaded / if none. */
export function usePageContentOverride(pageKey) {
    const [override, setOverride] = useState(_cache ? _cache[pageKey] : undefined);
    useEffect(() => {
        let alive = true;
        fetchOverrides().then((map) => {
            if (alive) setOverride(map[pageKey]);
        });
        return () => {
            alive = false;
        };
    }, [pageKey]);
    return override;
}

/** Get the whole override map (for components that render many pageKeys, e.g. grids). */
export function usePageContentOverrideMap() {
    const [map, setMap] = useState(_cache || {});
    useEffect(() => {
        let alive = true;
        fetchOverrides().then((m) => {
            if (alive) setMap(m);
        });
        return () => {
            alive = false;
        };
    }, []);
    return map;
}

// Optional: let the admin panel invalidate the cache after saving so a
// subsequent navigation in the same session reflects the change.
export function invalidatePageContentCache() {
    _cache = null;
    _cachePromise = null;
}
